import {request} from "services/request";
import {EnvSetup, getSupportUrl} from "enums/Envs";
import {AppIds, Apps, GenderName, Genders} from "enums";
import {AbuseStats} from "services/statsService";

export enum PatternTypes {
    About = 1,
    Photo = 2,
    Nickname = 3,
    Chat = 4,
    Question = 5,
    Icebreaker = 6
}

export const PatternTypesLabels: Record<PatternTypes, string> = {
    [PatternTypes.About]: 'О себе',
    [PatternTypes.Photo]: 'Фото',
    [PatternTypes.Nickname]: 'Имя',
    [PatternTypes.Chat]: 'Сообщение',
    [PatternTypes.Question]: 'Вопрос (Spark)',
    [PatternTypes.Icebreaker]: 'Ice Breaker (Spark)',
}

export enum ActionTypes {
    Hide = 2,
    Ban = 3,
    AdminCheck = 4,
    NeedsVerification = 5,
    Suspicious = 6,
    ShadowBan = 7,
    Reject = 8
}

export enum AbuseOptionsEmoji {
    Delimiters = '⁉️',
    IgnoreCase = '🆎',
    Multiline = '☰',
    SeparateWord = '【】'
}

export const ActionTypesLabels = (app?: Apps): Record<ActionTypes | number, string> => {
    return {
        1: 'Ничего',
        [ActionTypes.Hide]: 'Скрытие',
        [ActionTypes.Ban]: 'Бан',
        [ActionTypes.AdminCheck]: 'Сбросить ревью с тегом',
        [ActionTypes.NeedsVerification]: 'Отправить на верификацию',
        [ActionTypes.Suspicious]: 'Suspicious',
        [ActionTypes.ShadowBan]: 'Теневой бан',
        [ActionTypes.Reject]: 'Запрет на ввод',
    }
}

export interface AbuseWord {
    tag: string | null;
    language: string | null;
    appIds?: AppIds[]

    allowIgnoreCase: boolean;
    ruleName: string;
    allowSeparateWord: boolean;

    patternTypes?: PatternTypes[];

    actionType: ActionTypes;
    genders: Genders[];
    pattern: string;
    allowDelimiters: boolean;
    allowMultiline: boolean;
    id: number;
    stats?: AbuseStats;
    activeCheck?: AbuseRuleCheck | null;

    lastChanged?: {
        lastChangedDate: Date
        userKey: string
        userName: string
    }
}

export enum AbuseCheckStatus {
    Pending = 1,
    InProgress = 2,
    Completed = 3,
    Cancelled = 4,
    Failed = 5,
}

export interface AbuseRuleCheck {
    checkId: number;
    status: AbuseCheckStatus;
    totalCount: number;
    processedCount: number;
    matchedCount: number;
    progressPercent: number;
    startedDate: string;
    completedDate?: string | null;
    errorMessage?: string | null;
}

/** Row in POST …/abuse/preview response (shape may vary by backend). */
export type AbuseRulePreviewUserRow = {
    userId?: number;
    id?: number;
    userKey?: string;
    key?: string;
    name?: string;
    gender?: string | number;
    userGender?: string | number;
};

export type AbuseRulePreviewDisplayRow =
    | { type: 'text'; text: string }
    | {
    type: 'user';
    profileKey: string;
    userKey?: string;
    numericId?: number;
    name?: string;
    gender?: unknown;
};

export type AbuseRulePreviewResponse = {
    userIds?: number[];
    users?: AbuseRulePreviewUserRow[];
    matchedUsers?: AbuseRulePreviewUserRow[];
    items?: AbuseRulePreviewUserRow[];
    totalCount?: number;
    total?: number;
    matchedCount?: number;
    totalMatched?: number;
};

const genderCodeStrings = new Set<string>(Object.values(Genders));

const previewRowFromUserObject = (r: AbuseRulePreviewUserRow): AbuseRulePreviewDisplayRow => {
    const key = r.userKey ?? r.key;
    const id = r.userId ?? r.id;
    const name = r.name;
    const gender = r.gender ?? r.userGender;
    const profileKey = key ?? (id != null ? String(id) : '');
    if (profileKey) {
        return {
            type: 'user',
            profileKey,
            ...(key ? {userKey: key} : {}),
            ...(id != null ? {numericId: id} : {}),
            ...(name ? {name} : {}),
            ...(gender !== undefined ? {gender} : {}),
        };
    }
    try {
        return {type: 'text', text: JSON.stringify(r)};
    } catch {
        return {type: 'text', text: String(r)};
    }
};

/** Human-readable gender for preview rows (`Genders` code or raw value from API). */
export const formatPreviewRowGender = (gender: unknown): string | null => {
    if (gender == null || gender === '') {
        return null;
    }
    const code = typeof gender === 'number' ? String(gender) : typeof gender === 'string' ? gender : String(gender);
    if (genderCodeStrings.has(code)) {
        return GenderName[code as Genders];
    }
    return code;
};

/** Normalizes preview API payload for display (supports several common shapes). */
export const normalizeAbuseRulePreview = (data: unknown): { rows: AbuseRulePreviewDisplayRow[]; summary?: string } => {
    if (data == null) {
        return {rows: []};
    }
    if (Array.isArray(data)) {
        return {
            rows: data.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return previewRowFromUserObject(item as AbuseRulePreviewUserRow);
                }
                return {type: 'text', text: String(item)};
            }),
        };
    }
    if (typeof data !== 'object') {
        return {rows: [{type: 'text', text: String(data)}]};
    }
    const o = data as AbuseRulePreviewResponse;
    const rows: AbuseRulePreviewDisplayRow[] = [];
    if (o.userIds?.length) {
        o.userIds.forEach(id => {
            rows.push({
                type: 'user',
                profileKey: String(id),
                numericId: id,
            });
        });
    }
    const userRows = o.matchedUsers ?? o.users ?? o.items ?? [];
    userRows.forEach(r => rows.push(previewRowFromUserObject(r)));
    const summary =
        o.totalCount != null ? `В выборке: ${o.totalCount}`
            : o.matchedCount != null ? `Совпадений: ${o.matchedCount}`
                : o.total != null ? `Всего: ${o.total}`
                    : o.totalMatched != null ? `Всего совпадений: ${o.totalMatched}`
                        : undefined;
    return {rows, summary};
};

export const getTag = (name: string): string | null => {
    let tag = name.split(':');
    if (tag.length > 1) {
        return tag[0];
    }
    tag = name.split('-');
    if (tag.length > 1) {
        return tag[0]
    }
    return null;
}

export const getLanguage = (name: string): string | null => {
    const language = name.match(/\(([^)]+)\)/);
    if (language && language.length > 1) {
        return language[1];
    }
    return null;
}

/** True once the backend actually returns appIds (rolled out per-environment); drives whether the appIds UI shows at all. */
export const hasAppIdsSupport = (words: AbuseWord[]): boolean => {
    return words.some(w => w.appIds !== undefined);
}

export const abuseWordsService = {
    getAll: async (env: EnvSetup) => {
        return request<AbuseWord[]>({
            url: `/${getSupportUrl(env)}/abuse`,
            method: 'GET',
        }).then(r => r?.map(w => ({
            ...w,
            genders: w.genders.map(g => g.toString() as Genders),
            tag: getTag(w.ruleName),
            language: getLanguage(w.ruleName),
            name: w.ruleName,
            lastChanged: w.lastChanged ? {
                ...w.lastChanged,
                lastChangedDate: new Date(w.lastChanged.lastChangedDate)
            } : w.lastChanged,
            activeCheck: w.activeCheck ?? null
        }) as AbuseWord));
    },
    add: async (word: Partial<AbuseWord>, env: EnvSetup) => {
        return request<AbuseWord[]>({
            url: `/${getSupportUrl(env)}/abuse`,
            method: 'POST',
            data: {...word, genders: word.genders?.map(g => +g), appIds: word.appIds?.map(a => +a)}
        })
    },
    update: async (word: Partial<AbuseWord>, env: EnvSetup) => {
        return request<AbuseWord[]>({
            url: `/${getSupportUrl(env)}/abuse`,
            method: 'PUT',
            data: {...word, genders: word.genders?.map(g => +g), appIds: word.appIds?.map(a => +a)}
        })
    },
    delete: async (id: number, env: EnvSetup) => {
        return request<AbuseWord[]>({
            url: `/${getSupportUrl(env)}/abuse?id=${id}`,
            method: 'DELETE'
        })
    },
    test: async (text: string, word: Partial<AbuseWord>, env: EnvSetup) => {
        return request<string>({
            url: `/${getSupportUrl(env)}/abuse/testRule`,
            method: 'POST',
            data: {
                allowSeparateWord: word.allowSeparateWord,
                allowDelimiters: word.allowDelimiters,
                allowMultiline: word.allowMultiline,
                allowIgnoreCase: word.allowIgnoreCase,
                pattern: word.pattern,
                text,
            }
        })
    },
    startCheck: async (ruleId: number, env: EnvSetup) => {
        return request<AbuseRuleCheck>({
            url: `/${getSupportUrl(env)}/abuse/startCheck`,
            method: 'POST',
            data: {ruleId},
            withError: true
        })
    },
    getCheck: async (env: EnvSetup, params: { checkId?: number; ruleId?: number }) => {
        const query = params.checkId ? `id=${params.checkId}` : `ruleId=${params.ruleId}`;
        return request<AbuseRuleCheck>({
            url: `/${getSupportUrl(env)}/abuse/check?${query}`,
            method: 'GET',
            withError: true
        })
    },
    cancelCheck: async (checkId: number, env: EnvSetup) => {
        return request<void>({
            url: `/${getSupportUrl(env)}/abuse/check/cancel?id=${checkId}`,
            method: 'POST',
            withError: true
        })
    },
    /** Preview which users match the saved rule (no actions applied). limit: 1–1000, default 100. */
    preview: async (ruleId: number, limit: number, env: EnvSetup) => {
        const clamped = Math.min(1000, Math.max(1, Number.isFinite(limit) ? Math.floor(limit) : 100));
        return request<AbuseRulePreviewResponse | unknown>({
            url: `/${getSupportUrl(env)}/abuse/preview`,
            method: 'POST',
            data: {ruleId, limit: clamped},
        });
    },
}
